/**
 * Sprint 26.0 — the task-detail status pill (display only).
 *
 * Status is structural only — inbox / queued / done / dropped — never a runtime
 * verb (nothing executes asynchronously yet; design-rules bans runtime status
 * pills). "Current" is rank #1 in the queue (lowest sort_order); the parent
 * passes `isCurrent` so the queued pill can read "Current" instead of "Queue · #N".
 */

import type { CSSProperties } from "react";
import type { Task } from "../../../lib/types";

interface PillSpec {
  label: string;
  icon: string;
  bg: string;
  fg: string;
}

function deriveTaskStatusPill(t: Task, isCurrent?: boolean, queuePosition?: number): PillSpec {
  switch (t.status) {
    case "queued":
      if (isCurrent) {
        return {
          label: "Current",
          icon: "ti-arrow-bar-to-up",
          bg: "var(--c-purple-50)",
          fg: "var(--c-purple-800)",
        };
      }
      return {
        label: `Queue${queuePosition != null ? ` · #${queuePosition}` : ""}`,
        icon: "ti-list",
        bg: "var(--c-blue-50, var(--color-background-secondary))",
        fg: "var(--c-blue-800, var(--color-text-secondary))",
      };
    case "inbox":
      return {
        label: "Inbox",
        icon: "ti-inbox",
        bg: "var(--color-background-secondary)",
        fg: "var(--color-text-secondary)",
      };
    case "done":
      return { label: "Done", icon: "ti-check", bg: "var(--color-background-secondary)", fg: "var(--color-text-secondary)" };
    case "dropped":
      return { label: "Dropped", icon: "ti-x", bg: "var(--color-background-secondary)", fg: "var(--color-text-tertiary)" };
    default:
      return { label: String(t.status), icon: "ti-circle", bg: "var(--color-background-secondary)", fg: "var(--color-text-secondary)" };
  }
}

export function PaneStatusPill({ task, isCurrent, queuePosition }: { task: Task; isCurrent?: boolean; queuePosition?: number }) {
  const spec = deriveTaskStatusPill(task, isCurrent, queuePosition);
  return (
    <span style={{ ...pill, background: spec.bg, color: spec.fg }}>
      <i className={`ti ${spec.icon}`} style={{ fontSize: 11 }} aria-hidden="true" />
      {spec.label}
    </span>
  );
}

const pill: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "3px 8px",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: "0.4px",
};
